import { EmailSchema } from '@fundline/shared/schemas/members';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { TextField } from '../../components/ui/Field.tsx';
import { Brand, CenteredPage } from '../../components/ui/Layout.tsx';
import { captureAuthRedirectError } from '../../lib/auth-redirect.ts';
import { supabase } from '../../lib/supabase.ts';

type Mode = 'password' | 'link';

/** Email + password, or a one-time sign-in link by email. There is no sign-up: Fundline is invite-only. */
export function LoginPage() {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkSent, setLinkSent] = useState(false);
  const [redirectError] = useState(captureAuthRedirectError);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsedEmail = EmailSchema.safeParse(email);
    if (!parsedEmail.success) {
      setError('Enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'password') {
        const { error: signInError } = await supabase().auth.signInWithPassword({
          email: parsedEmail.data,
          password,
        });
        // On success the auth listener updates the session and the router leaves this page.
        if (signInError) {
          setError(
            signInError.status === 400
              ? 'Email or password is wrong.'
              : `Couldn't sign in: ${signInError.message}`,
          );
        }
      } else {
        const { error: otpError } = await supabase().auth.signInWithOtp({
          email: parsedEmail.data,
          options: { shouldCreateUser: false, emailRedirectTo: `${window.location.origin}/` },
        });
        // Unknown emails get the same answer as known ones (no account discovery).
        if (otpError && otpError.status !== 400 && otpError.status !== 422) {
          setError(
            otpError.status === 429
              ? 'Too many sign-in emails were requested. Wait a few minutes or use your password.'
              : `Couldn't send the link: ${otpError.message}`,
          );
        } else {
          setLinkSent(true);
        }
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <CenteredPage>
      <Brand />
      <div>
        <h1 className="title">Sign in</h1>
        <p className="mt-1 callout text-ink-muted">Fundline accounts are created by invitation.</p>
      </div>

      {redirectError ? <Alert tone="warning">{redirectError.message}</Alert> : null}

      <div
        className="grid grid-cols-2 gap-1 rounded-md bg-surface p-1"
        role="tablist"
        aria-label="Sign-in method"
      >
        {(
          [
            ['password', 'Password'],
            ['link', 'Email me a link'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={mode === value}
            onClick={() => {
              setMode(value);
              setError(null);
              setLinkSent(false);
            }}
            className="min-h-9 rounded-sm px-2 callout font-medium text-ink-muted focus-visible:outline-2 focus-visible:outline-focus-ring aria-selected:bg-surface-raised aria-selected:text-ink"
          >
            {label}
          </button>
        ))}
      </div>

      {linkSent ? (
        <Alert tone="success">
          If an account exists for {email.trim()}, a sign-in link is on its way. It works once and
          expires after 24 hours.
        </Alert>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
          <TextField
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {mode === 'password' ? (
            <TextField
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          ) : (
            <p className="footnote text-ink-muted">
              No password yet, or forgot it? We email you a one-time link. Then set a new password
              on your Account page.
            </p>
          )}
          {error ? <Alert tone="error">{error}</Alert> : null}
          <Button type="submit" busy={busy}>
            {mode === 'password' ? 'Sign in' : 'Send sign-in link'}
          </Button>
        </form>
      )}
    </CenteredPage>
  );
}
